// Updated: 2026-08-14
//
// Migrated to React Query in slice 7 (react-query-user-relations, Req 2, 3, 5, 6, 9, 10).
//
// ─── WHAT THIS PROVIDER IS NOW ────────────────────────────────────────────────────────────────────
//
// A THIN DERIVATION over ONE cache entry, `['me','relations']`, and nothing else. It owns no data: no
// `useState` for favorites, following, followers, moderator status, loading or error; no fetch effect; no
// `lastFetchedIdToken` dedup ref; no hand-copied fetch body inside `refreshUserRelations`. The cache holds
// the payload verbatim (`src/queries/userRelations/queries.ts`) and every consumer-facing member below is
// a pure function of it, computed at render time.
//
// It is kept — rather than deleted in favour of converting its eight consumers — because the blast radius
// of deleting it is the whole authenticated shell, and because two existing test files
// (`Moderation/__tests__/UserModerationSearchPage.test.tsx`, `UserAnalysis/__tests__/UserAnalysisPage.test.tsx`)
// mock `useUserRelations` WHOLESALE with a partial object literal. Keeping the adapter is what lets the
// transport move while the consumer surface — and those mocks — stay unchanged: six of the eight consumers
// change by zero lines.
//
// The name, the export, the position in `src/index.tsx`, and the THROWING `useUserRelations()` hook are all
// unchanged, deliberately. A component rendered outside the shell must keep failing loudly — slice 6's
// `queries/notification/__tests__/queries.test.tsx` renders the real `NotificationBell` with NO provider in
// the tree and relies on that throw as its assertion (Req 3.3). Softening the hook to return defaults would
// turn that expectation into a silent pass.
//
// ─── D1: THE SIGN-OUT PRIVILEGE LEAK NEEDS BOTH MECHANISMS ────────────────────────────────────────
//
// `isModerator` is an AUTHORIZATION signal, not a display value — `ModeratorRoute.tsx:43` decides route
// access on it and `Navigation` decides which links exist. Three facts make a retained entry a privilege
// leak rather than a staleness annoyance: `enabled: false` does not EVICT (React Query keeps returning the
// last successful `data` for the key); `gcTime`'s 10-minute timer only starts once the last observer
// unmounts, and this provider sits above `App` and never unmounts while the tab lives, so the entry is
// retained INDEFINITELY; and on a shared machine the next user signs in while that entry still says the
// previous one was a moderator.
//
// So there are TWO mechanisms below, and NEITHER IS SUFFICIENT ALONE — each closes a window the other
// leaves open (design.md § 3):
//
//   * **The derive-guard** (`const relations = enabled ? data : undefined`) blanks the surface on every
//     render where `enabled` is false, INCLUDING the pre-effect one. It is not sufficient by itself: the
//     entry survives in the cache, and at the next sign-in `enabled` flips back to true on the very first
//     authenticated render — token present, pathname fine — BEFORE that session's fetch resolves, so the
//     now-passing guard would hand back the PREVIOUS user's payload.
//   * **The sign-out purge** (`removeQueries` on the authenticated→unauthenticated transition) guarantees
//     that when `enabled` next becomes true there is nothing stale left to serve. It is not sufficient by
//     itself either: effects fire AFTER commit, so between the last authenticated render and the purge
//     there is at least one render where `isAuthenticated` is already false while the cache still holds the
//     payload — moderator links painted to a signed-out user.
//
// The purge is scoped to `userRelationsKeys.all()` and to nothing else. A broad
// `removeQueries({ queryKey: ['me'] })` from here would silently take over a responsibility slice 9
// (`AuthContext`) is designed to own — the general `['me',…]`-wide purge — leaving two purge sites to keep
// in agreement. This is BL-002 in its highest-severity instance, closed for THIS key only; slice 6's
// `['me','notifications','unreadCount']` still has the same hole.

import { useQueryClient } from '@tanstack/react-query'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { userRelationsKeys } from '../queries/userRelations/keys'
import { useUserRelationsQuery } from '../queries/userRelations/queries'
import { UserRelationsContextType } from '../types/types'

import { useAuth } from './AuthContext'

const UserRelationsContext = createContext<UserRelationsContextType | undefined>(undefined)

export const UserRelationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, idToken, signOut } = useAuth()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()

  /**
   * THE GATE — the pre-migration effect's four early returns, collapsed into one boolean (Req 2.3).
   *
   * `pathname` comes from `useLocation()`, not `window.location.pathname`. The old read was invisible to
   * React: it was re-evaluated only when the effect happened to run, so leaving `/create/profile` did not
   * by itself permit a fetch. Reactive now, which is also the RECOVERY PATH after profile creation — the
   * dedup ref that used to be reset to "permit" a retry is gone, and a pathname change makes the retry
   * actually HAPPEN, on the exact transition that resolves the cause. The provider already sits under
   * `BrowserRouter` in `src/index.tsx`, so nothing moves to get this.
   *
   * The fourth early return — the `lastFetchedIdToken` dedup — has no conjunct here on purpose: THE CACHE
   * IS THE DEDUP, and a strictly better one (shared in-flight requests, deliberate invalidation,
   * deliberate eviction).
   */
  const enabled = isAuthenticated && !!idToken && pathname !== '/create/profile'

  const { data, isPending, error } = useUserRelationsQuery({ idToken, enabled })

  /**
   * THE DERIVE-GUARD — half of D1. Every member below reads `relations`, NEVER `data`.
   *
   * `enabled: false` does not evict, so `data` can still be the previous session's payload. Reading `data`
   * directly anywhere below re-opens the privilege leak this line exists to close.
   */
  const relations = enabled ? data : undefined

  // The `Array.isArray(...) ? ... : []` coercions the old provider baked into `useState` initialisers and
  // `.then` handlers now happen HERE, at derivation time (Req 1.8). The old provider stored `favoriteIds`
  // AND `favoriteSet` AND the response — three copies of one truth, and an optimistic patch that hit one
  // of them left consumers reading the others with a different answer. One cached payload cannot disagree
  // with itself.
  const favoriteSet = useMemo(() => {
    const ids = relations?.favoriteIds
    return new Set<string>(Array.isArray(ids) ? ids : [])
  }, [relations])

  const followingSet = useMemo(() => {
    const names = relations?.followingScreenNames
    return new Set<string>(Array.isArray(names) ? names : [])
  }, [relations])

  const isFavorited = useCallback((patternId: string) => favoriteSet.has(patternId), [favoriteSet])
  const isFollowing = useCallback((screenName: string) => followingSet.has(screenName), [followingSet])

  const isModerator = relations?.isModerator ?? false

  // BL-001 PRESERVED, not fixed: the handler never sends this field
  // (`lambda-secure/src/routes/get-user-relations.ts:133-143`), so the Craft Room drafts panel stays
  // empty — exactly as the old provider's never-overwritten `useState([])` left it.
  const projects = relations?.unpublishedProjects
  const unpublishedProjects = useMemo(() => (Array.isArray(projects) ? projects : []), [projects])

  /**
   * D3 — `loading` is now TRUTHFUL on the first enabled render (Req 5.1).
   *
   * The old value was `useState(false)` with `setLoading(true)` buried in the effect AFTER four early
   * returns, so React committed a render reporting `loading: false` and `isModerator: false` at the same
   * time — and `ModeratorRoute` reads exactly that pair: the spinner gate was skipped and a genuine
   * moderator was redirected to `/` before the request that would have proved they were one had even been
   * issued (BL-008). `isPending` is computed by React Query DURING render from the entry's state, so the
   * spinner is up before anything paints.
   *
   * The `enabled` conjunct is load-bearing in the other direction: without it a signed-out user (whose
   * query is disabled and therefore permanently `isPending`) would hang `ModeratorRoute`'s spinner
   * forever. With it, the disabled paths report `false` — byte-identical to `useState(false)` on the paths
   * where the old `setLoading(true)` was never reached. The new `loading` is `true` on a SUPERSET of the
   * renders where the old one was true, so a redirect can only ever be deferred, never issued earlier.
   */
  const loading = enabled ? isPending : false

  /**
   * THE SIGN-OUT PURGE — the other half of D1, scoped to THIS key only.
   *
   * Transition detection is a ref holding the PREVIOUS `isAuthenticated`, compared and then updated inside
   * the effect. It must be a transition and not simply `if (!isAuthenticated)`: the latter would also fire
   * on the very first mount of an anonymous session and on every unrelated re-render while signed out,
   * turning a one-shot eviction into a repeated one. Only authenticated→unauthenticated purges.
   */
  const wasAuthenticated = useRef(isAuthenticated)
  useEffect(() => {
    const previouslyAuthenticated = wasAuthenticated.current
    wasAuthenticated.current = isAuthenticated

    if (previouslyAuthenticated && !isAuthenticated) {
      queryClient.removeQueries({ queryKey: userRelationsKeys.all() })
    }
  }, [isAuthenticated, queryClient])

  /**
   * THE 401 SIGN-OUT — at most once per DISTINCT error instance (Req 10.2).
   *
   * React Query v5's `useQuery` has no `onError` callback, so there is nowhere declarative to hang this,
   * and a render-body call is wrong for a concrete reason: the cache entry HOLDS the error, so
   * `if (is401) signOut()` in the render body fires on every render for as long as the entry stays errored
   * — and `signOut()` sets state in `AuthContext`, which re-renders this provider, which calls it again.
   *
   * Keyed on the error INSTANCE, not on a boolean. A boolean would LATCH: it stays `true` across
   * re-renders so the effect never re-runs, and a second, distinct 401 in a later session — new `Error`
   * object, same truthiness — would be swallowed. Instance identity gives at-most-once per failure:
   * idempotent for a held error, live for a new one.
   *
   * THE PROFILE-LESS 404 STAYS SILENT AND NON-NAVIGATING, and that abstention is load-bearing. A caller
   * with no profile row gets **404** carrying the typed discriminant `error: 'profile_required'` — the
   * `ApiErrorCode` union in `@mrb91002/shared-types`, which all three profile-less handlers are
   * coordinated on (`auth-contract-correctness` Req 1, 2). Its `message` is DISPLAY TEXT with no
   * control-flow role: reword it however you like, nothing in this file reads it. The 401 test below
   * compares the NUMERIC status, so a 404 is simply not a 401 and nothing happens here — no `error`
   * member on the six-member surface, and NO REDIRECT.
   *
   * `AuthContext.initAuth` is the single decider that routes a profile-less user to `/create/profile`;
   * when this provider also redirected, brand-new accounts hit a redirect loop between the two deciders.
   * Do not add a navigation to this effect.
   */
  const handledError = useRef<unknown>(null)
  useEffect(() => {
    if (!error || error === handledError.current) return
    handledError.current = error

    // The 401 test is the ONLY branch, and it reads the NUMERIC status STRUCTURALLY — the same `status`
    // property the shared retry predicate reads (`queries/queryClient.ts:37`), surfaced by both transports
    // (`ApiError` from `secureFetch`, `PublicApiError` from the public fetcher). The former substring test
    // on `message` is DELETED, not kept as a fallback: it matched any message merely containing the
    // characters `401` and missed every real 401, because `ApiError.message` is the server's `message`
    // field verbatim and carries no status text. A profile-less 404 falls through this branch and nothing
    // happens — the whole of "silent and non-navigating".
    if ((error as { status?: number } | null | undefined)?.status === 401) void signOut()
  }, [error, signOut])

  /**
   * Still a zero-arg `() => void`, so `CreatePattern` — its only consumer — changes by zero lines. The body
   * is now ONE invalidation instead of a hand-copied duplicate of the fetch-and-set block (Req 9.1), and it
   * is scoped to `['me','relations']`: a `queryKey: ['me']` typo here would refetch every personal query in
   * the app on every pattern submit.
   */
  const refreshUserRelations = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: userRelationsKeys.all() })
  }, [queryClient])

  // EXACTLY SIX MEMBERS — one per live read across the eight consumers (Req 4.1). The structural test
  // asserts `Object.keys(value).sort()`, so adding a member without a consumer FAILS, which is what keeps
  // slice 6's three notification fields (D2) from creeping back in.
  const value = useMemo<UserRelationsContextType>(
    () => ({
      isFavorited,
      isFollowing,
      isModerator,
      loading,
      unpublishedProjects,
      refreshUserRelations,
    }),
    [isFavorited, isFollowing, isModerator, loading, unpublishedProjects, refreshUserRelations],
  )

  return <UserRelationsContext.Provider value={value}>{children}</UserRelationsContext.Provider>
}

// ---- Hook ----
// THROWS outside its provider, deliberately and permanently. See the module header: slice 6's notification
// query tests use that throw as an assertion, and a component rendered outside the authenticated shell must
// fail loudly rather than silently read defaults.
export function useUserRelations() {
  const context = useContext(UserRelationsContext)
  if (!context) {
    throw new Error('useUserRelations must be used within a UserRelationsProvider')
  }
  return context
}
